import {
  faClapperboardPlay,
  faSignalStream,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssistChip, Stack, Step, Stepper } from '@sixui/ui';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetLiveStreamsItemResponseBody } from '~/lib/api/modules/LiveStreams';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { LiveStreamRecordingListItem } from './LiveStreamRecordingListItem';

export type ILiveStreamRecordingListProps = IContainerProps & {
  liveStream: IApiGetLiveStreamsItemResponseBody;
};

export const LiveStreamRecordingList: React.FC<
  ILiveStreamRecordingListProps
> = (props) => {
  const { sx, liveStream } = props;
  const { t, i18n } = useTranslation();

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>
        {t('LiveStreamRecordingList.title')}
      </ContentBlock.Title>

      <ContentBlock.Body>
        {liveStream.recordings?.length ? (
          <Stepper
            orientation="vertical"
            completed
            // There is a visual glitch in the connector. When the stepper is
            // completed, the color of the connector is not correct at its end. This
            // is a workaround to make it appear correctly.
            activeStep={liveStream.recordings?.length}
          >
            {liveStream.recordings
              ?.slice()
              .reverse()
              .map((recording, recordingIndex) => (
                <Step
                  key={recordingIndex}
                  label={
                    <Stack horizontal gap={2}>
                      {(recording.state === 'started' ||
                        recording.state === 'failed') && (
                        <AssistChip
                          icon={
                            recording.state === 'started' ? (
                              <FontAwesomeIcon icon={faSignalStream} />
                            ) : (
                              <FontAwesomeIcon icon={faTriangleExclamation} />
                            )
                          }
                          label={
                            recording.state === 'started'
                              ? t('LiveStreamRecordingList.state.started')
                              : t('LiveStreamRecordingList.state.failed')
                          }
                        />
                      )}
                      {t('LiveStreamRecordingList.stepTitle', {
                        start:
                          recording.start &&
                          DateTime.fromISO(recording.start).toLocaleString(
                            DateTime.DATETIME_MED,
                            { locale: i18n.language },
                          ),
                        duration:
                          recording.end &&
                          DateTime.fromISO(recording.end).toLocaleString(
                            DateTime.DATETIME_MED,
                            { locale: i18n.language },
                          ),
                      })}
                    </Stack>
                  }
                  icon={<FontAwesomeIcon icon={faClapperboardPlay} />}
                  alwaysExpanded
                >
                  <LiveStreamRecordingListItem
                    liveStream={liveStream}
                    recording={recording}
                  />
                </Step>
              ))}
          </Stepper>
        ) : (
          t('LiveStreamRecordingList.empty')
        )}
      </ContentBlock.Body>
    </ContentBlock>
  );
};
